import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  debounce,
  Divider,
  InputAdornment,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import apiService from "@/api/ApiService";
import { ProjectImage } from "./types";
import { GalleryGrid } from "./components/GalleryGrid";
import { GalleryDetail } from "./components/GalleryDetail";
import { GalleryUploader } from "./components/GalleryUploader";
import { TopBar } from "@/shared/structure/TopBar";
import { MainContainer } from "@/shared/structure/MainContainer";
import { Add, ArrowBack, ArrowLeft, Download, SelectAll } from "@mui/icons-material";
import { filter } from "lodash";
import ApiService from "@/api/ApiService";
import { AxiosResponse } from "axios";
import { LoadingButton } from "@mui/lab";
import { Search } from "lucide-react";
import dayjs from "dayjs";
import { imageUrl } from "./utils";

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseDateToken(token: string): { from?: dayjs.Dayjs; to?: dayjs.Dayjs } | null {
  const t = token.trim();

  // Range: DD.MM.YYYY-DD.MM.YYYY
  const range = t.match(/^(\d{2}\.\d{2}\.\d{4})-(\d{2}\.\d{2}\.\d{4})$/);
  if (range) {
    const from = dayjs(range[1], "DD.MM.YYYY", true);
    const to = dayjs(range[2], "DD.MM.YYYY", true);
    if (!from.isValid() || !to.isValid()) return null;

    const a = from.startOf("day");
    const b = to.endOf("day");
    return a.isBefore(b) ? { from: a, to: b } : { from: b.startOf("day"), to: a.endOf("day") };
  }

  // Single date: DD.MM.YYYY
  const single = t.match(/^(\d{2}\.\d{2}\.\d{4})$/);
  if (single) {
    const d = dayjs(single[1], "DD.MM.YYYY", true);
    if (!d.isValid()) return null;
    return { from: d.startOf("day"), to: d.endOf("day") };
  }

  return null;
}

function imageLocalDate(img: ProjectImage) {
  // your buckets use utc(...).local(), so keep date filtering consistent
  return dayjs.utc(img.madeAt ?? img.createdAt, "YYYY-MM-DD HH:mm:ss").local();
}

export function Gallery({ projectId, writeable = false }: { projectId: number; writeable?: boolean }) {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const gridScrollTop = useRef(0);
  const [uploaderOpen, setUploaderOpen] = useState(false);

  useLayoutEffect(() => {
    if (selectedImage === null && gridRef.current) {
      gridRef.current.scrollTop = gridScrollTop.current;
    }
  }, [selectedImage]);

  const [selectionActive, setSelectionActive] = useState<boolean>(false);
  const [selected, setSelected] = useState<number[]>([]);

  const [searchInput, setSearchInput] = useState("");
  const [searchString, setSearchString] = useState("");

  const handleSearchChange = useMemo(
    () =>
      debounce((val) => {
        setSearchString(val);
      }, 500),
    [],
  );
  const handleSearchInput = (e) => {
    const val = (e.target as HTMLInputElement).value;
    setSearchInput(val);
    handleSearchChange(val);
  };

  const getZIPMutation = useMutation({
    mutationFn: ApiService.getGalleryZip(), // POST /data/galleryZip/request
    onSuccess: (response) => {
      const token = response?.token;
      if (!token) return;

      // Base URL: import.meta.env.VITE_APP_URL contains ".../api" (everything until domain.com/api)
      const base = import.meta.env.VITE_APP_URL.replace(/\/$/, ""); // no trailing slash

      // If your download route is also under /api, this becomes: https://domain.com/api/data/galleryZip/download/<token>
      const downloadUrl = `${base}/data/galleryZip/download/${encodeURIComponent(token)}`;

      // Trigger native download (streaming, shows in browser downloads)
      window.location.assign(downloadUrl);
    },
  });

  const imagesQuery = useQuery<ProjectImage[]>({
    queryKey: ["getProjectImages", String(projectId)],
    queryFn: apiService.getEntitiesWithFilter("projectImage", { project: ["project", projectId], deletedAt: null }, "shortInfo"),
    select: (data) =>
      [...data].sort((a, b) => {
        const da = a.madeAt ?? a.createdAt;
        const db = b.madeAt ?? b.createdAt;
        return db > da ? 1 : -1;
      }),
  });

  const images = useMemo(() => imagesQuery.data ?? [], [imagesQuery.data]);

  const filteredImages = useMemo(() => {
    const raw = (searchString ?? "").trim();
    if (!raw) return images;

    // split by whitespace, treat as AND
    const tokens = raw.split(/\s+/).filter(Boolean);

    return images.filter((img) => {
      const desc = (img.description ?? "").toLowerCase();
      const createdByName = (img.createdBy?.name ?? "").toLowerCase();
      const createdByFirst = (img.createdBy?.firstName ?? "").toLowerCase();

      const date = imageLocalDate(img);
      const dateStr = date.format("DD.MM.YYYY"); // helps for non-strict date tokens too

      for (const token of tokens) {
        const dateFilter = parseDateToken(token);
        if (dateFilter) {
          const { from, to } = dateFilter;
          if (from && date.isBefore(from)) return false;
          if (to && date.isAfter(to)) return false;
          continue;
        }

        const t = token.toLowerCase();

        // If token contains a date somewhere inside (e.g. "foo12.03.2026"),
        // try to extract and apply it intelligently.
        const embeddedRange = token.match(/(\d{2}\.\d{2}\.\d{4})-(\d{2}\.\d{2}\.\d{4})/);
        if (embeddedRange) {
          const embedded = parseDateToken(`${embeddedRange[1]}-${embeddedRange[2]}`);
          if (embedded) {
            const { from, to } = embedded;
            if (from && date.isBefore(from)) return false;
            if (to && date.isAfter(to)) return false;
            continue;
          }
        }

        const embeddedSingle = token.match(/(\d{2}\.\d{2}\.\d{4})/);
        if (embeddedSingle) {
          const embedded = parseDateToken(embeddedSingle[1]);
          if (embedded) {
            const { from, to } = embedded;
            if (from && date.isBefore(from)) return false;
            if (to && date.isAfter(to)) return false;
            continue;
          }
        }

        // normal text matching (description, createdBy, and date string)
        const re = new RegExp(escapeRegExp(t), "i");
        const textMatch = re.test(desc) || re.test(createdByName) || re.test(createdByFirst) || re.test(dateStr);

        if (!textMatch) return false;
      }

      return true;
    });
  }, [images, searchString]);

  const handleSelect = (input: number | number[]) => {
    if (!selectionActive) {
      const id = Array.isArray(input) ? input[0] : input;
      if (id !== undefined) {
        gridScrollTop.current = gridRef.current?.scrollTop ?? 0;
        setSelectedImage(id);
      }
      return;
    }

    if (Array.isArray(input)) {
      setSelected((prev) => [...prev, ...input.filter((id) => !prev.includes(id))]);
      return;
    }

    const id = input;

    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      {writeable ? (
        selectedImage ? null : (
          <TopBar>
            {selectionActive
              ? [
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => {
                      setSelectionActive(false);
                      setSelected([]);
                    }}
                  >
                    zurück
                  </Button>,
                  <Button
                    startIcon={<SelectAll />}
                    onClick={() =>
                      setSelected((prev) => {
                        const set = new Set(prev);
                        filteredImages.forEach((f) => set.add(f.id));
                        return Array.from(set);
                      })
                    }
                  >
                    Alle
                  </Button>,
                  <LoadingButton
                    startIcon={<Download />}
                    disabled={selected.length === 0}
                    loading={getZIPMutation.isPending}
                    onClick={() =>
                      getZIPMutation.mutate(
                        filteredImages
                          .filter((x) => selected.includes(x.id))
                          .map((x) => ({
                            file: x.filename,
                            description: x.description,
                            time: dayjs
                              .utc(x.madeAt || x.createdAt)
                              .local()
                              .format("YYYY-MM-DD_HH-mm-ss"),
                            name: x.originalFilename,
                          })),
                      )
                    }
                  >
                    Herunterladen
                  </LoadingButton>,
                ]
              : [
                  <Button startIcon={<Add />} onClick={() => setUploaderOpen(true)}>
                    Hinzufügen
                  </Button>,
                  <Button startIcon={<SelectAll />} onClick={() => setSelectionActive(true)}>
                    Auswählen
                  </Button>,
                ]}
            <Box sx={{ flex: 1 }} />
            <OutlinedInput
              size="small"
              onChange={handleSearchInput}
              value={searchInput}
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              }
            />
          </TopBar>
        )
      ) : null}

      {imagesQuery.isLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
          <CircularProgress />
        </Stack>
      ) : imagesQuery.isError ? (
        <Typography color="error" variant="body1">
          Die Galerie konnte nicht geladen werden.
        </Typography>
      ) : selectedImage ? (
        <Box sx={{ overflow: "hidden", display: "flex", flex: 1, flexDirection: "column", background: "#eee", padding: 2 }}>
          <GalleryDetail images={filteredImages} startImageId={selectedImage} onClose={() => setSelectedImage(null)} />
        </Box>
      ) : (
        <Box ref={gridRef} sx={{ overflow: "auto", background: "#eee", padding: 2 }}>
          <Paper sx={{ p: 2 }}>
            <GalleryGrid selected={selected} selectMode={selectionActive} images={filteredImages} onSelect={handleSelect} />
          </Paper>
        </Box>
      )}
      <GalleryUploader projectId={projectId} open={uploaderOpen} onClose={() => setUploaderOpen(false)} />
    </Box>
  );
}

export default Gallery;
